import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { ClientProduct, WorkOrderContact } from '@/features/client-workorder/types';
import { getClientProduct } from '@/features/client-workorder/api/getClientProduct';

type ContactFormType = {
  name: string;
  mail: string;
  tel: string;
  role: string;
  mobile: string;
  product: ClientProduct;
};

type ContactModalProps = {
  wo_id?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  contact?: WorkOrderContact;
  onSubmit: (data: Omit<WorkOrderContact, 'id' | 'wo_id' | 'product'>) => void;
};

const CustomerContactModal = ({
  isOpen,
  setIsOpen,
  contact,
  onSubmit,
  wo_id,
}: ContactModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [showProductsPicker, setShowProductsPicker] = useState(contact?.role === 'AUD');
  const { t } = useTranslation();

  const form = useForm<ContactFormType>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      mail: '',
      tel: '',
      role: '',
      mobile: '',
      product: {
        name: '',
        id: '',
      },
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    } else {
      form.reset({
        name: contact?.name || '',
        mail: contact?.mail || '',
        tel: contact?.tel || '',
        mobile: contact?.mobile || '',
        role: contact?.role || '',
        product: contact?.product || { name: '', id: '' },
      });
      setShowProductsPicker(contact?.role === 'AUD');
    }
  }, [isOpen, form, contact]);

  const roleWatch = form.watch('role');

  useEffect(() => {
    if (roleWatch === 'AUD') {
      setShowProductsPicker(true);
    } else {
      setShowProductsPicker(false);
    }
  }, [roleWatch]);

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };
  const baseSubmit = (data: ContactFormType) => {
    onSubmit(data);
    handleClose();
  };
  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle id="responsive-dialog-title">Title</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={form.handleSubmit(baseSubmit)}
          noValidate
          sx={{ mt: '1rem' }}
        >
          <Controller
            control={form.control}
            name="name"
            rules={{
              required: t('Name is required.', { defaultValue: 'Name is required.' }).toString(),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="name"
                fullWidth
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="mail"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Email', { defaultValue: 'Email' })}
                fullWidth
                error={!!form.formState.errors.mail}
                helperText={form.formState.errors.mail?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="tel"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Phone', { defaultValue: 'Phone' })}
                fullWidth
                error={!!form.formState.errors.tel}
                helperText={form.formState.errors.tel?.message?.toString()}
              />
            )}
          />
          <Controller
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Mobile', { defaultValue: 'Mobile' })}
                fullWidth
                error={!!form.formState.errors.mobile}
                helperText={form.formState.errors.mobile?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="role"
            render={({ field }) => (
              <>
                <InputLabel id="role-label">{t('Role', { defaultValue: 'Role' })}</InputLabel>
                <Select
                  {...field}
                  labelId="role-label"
                  id="role"
                  label={t('Role', { defaultValue: 'Role' })}
                  fullWidth
                >
                  <MenuItem value="DIR">{t('DIR', { defaultValue: 'DIR' })}</MenuItem>
                  <MenuItem value="AUD">{t('AUD', { defaultValue: 'AUD' })}</MenuItem>
                  <MenuItem value="FIN">{t('FIN', { defaultValue: 'FIN' })}</MenuItem>
                  <MenuItem value="PORTAL">{t('PORTAL', { defaultValue: 'PORTAL' })}</MenuItem>
                  <MenuItem value="OTH">{t('OTH', { defaultValue: 'OTH' })}</MenuItem>
                </Select>
              </>
            )}
          />
          {showProductsPicker && (
            <Box sx={{ mt: '1rem' }}>
              <AsyncAutocomplete<ClientProduct>
                id="product-list"
                label={t('Link to the product', { defaultValue: 'Link to the product' })}
                fullWidth
                getData={async (search) => {
                  const response = await getClientProduct(wo_id || '', {
                    page: 1,
                    pageSize: 100,
                    search,
                  });

                  return response.results;
                }}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                selectedOption={form.getValues('product')}
                setSelectedOption={(option) => {
                  form.setValue('product', option || { id: '', name: '' }, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit(baseSubmit)();
            }}
            fullWidth
            sx={{ mt: '1rem' }}
          >
            {t('Save', { defaultValue: 'Save' })}
          </Button>

          {/* <Button
            variant="contained"
            type="reset"
            fullWidth
            sx={{ mt: '1rem' }}
            onClick={deleteAction}
          >
            {t('Delete', { defaultValue: 'Delete' })}
          </Button> */}

          <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>

          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerContactModal;
